import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type AccountSummary } from '../api/client';

const KEY = 'atlas.account_id';
const listeners = new Set<() => void>();

const store = {
  get(): number | null {
    const raw = localStorage.getItem(KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  },
  set(id: number) {
    localStorage.setItem(KEY, String(id));
    listeners.forEach((l) => l());
  },
  subscribe(fn: () => void) {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
};

function useStoredAccountId(): number | null {
  return useSyncExternalStore(store.subscribe, store.get, () => null);
}

export interface AccountsState {
  accounts: AccountSummary[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** The account every account-scoped screen reads. Falls back to the first. */
  selectedId: number | null;
  selected: AccountSummary | null;
  select: (id: number) => void;
}

export function useAccounts(): AccountsState {
  const query = useQuery({ queryKey: ['accounts'], queryFn: api.accounts, staleTime: 60_000 });
  const accounts = query.data?.items ?? [];
  const stored = useStoredAccountId();

  const selectedId =
    stored != null && accounts.some((a) => a.id === stored)
      ? stored
      : (accounts[0]?.id ?? stored ?? null);

  return {
    accounts,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    selectedId,
    selected: accounts.find((a) => a.id === selectedId) ?? null,
    select: store.set,
  };
}
